var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};

// src/utils/pb_iframe_api.js
var PBIframeApi = class {
  constructor(target) {
    /**
     * Stores the response promises
     */
    __publicField(this, "responsePromises", {});
    /**
     * Stores the notification handlers
     */
    __publicField(this, "notificationHandlers", {});
    __publicField(this, "connectionPromise", null);
    __publicField(this, "initPromise", null);
    __publicField(this, "pbinfo", false);
    this.target = target;
    window.addEventListener("message", (event) => {
      if (event.data.target !== "pb") {
        return;
      }
      if (event.data.type === "pbinfo") {
        this.pbinfo = event.data;
      }
      if (event.data.type === "response") {
        const responsePromiseSettlers = this.responsePromises[event.data.id];
        if (!responsePromiseSettlers) {
          throw new Error(`Missing handler for response ${event.data.id}, perhaps due to an earlier timeout`);
        }
        if (event.data.error === false) {
          responsePromiseSettlers.resolve(event.data);
        } else {
          responsePromiseSettlers.reject(event.data);
        }
        clearTimeout(responsePromiseSettlers.timeout);
        delete this.responsePromises[event.data.id];
        return;
      }
      const notificationType = event.data.type || "*";
      const notificationHandlers = [
        ...Object.values(this.notificationHandlers[notificationType] || {}),
        ...Object.values(this.notificationHandlers["*"] || {})
      ];
      for (let notificationHandler of notificationHandlers) {
        notificationHandler(event.data);
      }
    });
  }
  /**
   * Connects to the target
   * This now works through polling, bc the iframe can be loaded before this script
   * Would be better to have this script create the iFrame, then a simple up-message from
   * the target will always do
   * @returns {Promise<Object<string,any>>}
   */
  async connect() {
    const client = this;
    let timedOut = false;
    let res = null;
    let rej = null;
    const connected = new Promise(function(resolve, reject) {
      res = resolve;
      rej = reject;
    });
    const connectionTimeout = setTimeout(function() {
      timedOut = true;
      rej("PB Iframe Api could not connect: target does not respond");
    }, 6e4);
    while (timedOut !== true) {
      this.target.contentWindow.postMessage(
        "pbping",
        new URL(this.target.src).origin
      );
      await new Promise((r) => setTimeout(r, 100));
      if (typeof this.pbinfo === "object") {
        clearTimeout(connectionTimeout);
        if (this.pbinfo.originAllowed !== true) {
          rej(`PB Iframe Api could not connect: origin [${this.pbinfo.origin}] is not allowed`);
          return connected;
        }
        res(client);
        break;
      }
    }
    await this.request("subscribe", "*");
    return connected;
  }
  /**
   * Create an id (string of semi-random numbers)
   * @returns {string} id
   */
  createId() {
    return String(Math.random()).substr(2);
  }
  /**
   * Request information or action from a PB app
   * @param {string} method
   * @param {any} data
   * @param {number} [timeoutTime = 5000]
   * @returns {Promise<Response>}
   */
  request(method, data, timeoutTime = 15 * 1e3) {
    if (typeof this.pbinfo !== "object") {
      throw new Error("Please .connect() to target before doing requests");
    }
    if (this.pbinfo.originAllowed !== true) {
      throw new Error("Origin not allowed");
    }
    const id = this.createId();
    const client = this;
    this.target.contentWindow.postMessage(
      {
        id,
        target: "pb",
        method,
        data
      },
      new URL(this.target.src).origin
    );
    const onTimeout = function() {
      client.responsePromises[id].reject("Timeout");
    };
    const timeout = setTimeout(onTimeout, timeoutTime);
    const returnPromise = new Promise(function(resolve, reject) {
      client.responsePromises[id] = {
        resolve,
        reject,
        timeout
      };
    });
    return returnPromise;
  }
  /**
   * Subscribe to notifications coming from a PB app
   * @param {string} [event = '*'] - Asterisk '*' subscribes to all events
   * @param {function} handler
   * @returns {function} deleteHandler - call this function to stop listening
   */
  onEvent(handler) {
    const id = this.createId();
    if (typeof handler !== "function") {
      throw new Error('Expected handler to be of type "function"');
    }
    const saneNTName = "*";
    const handlers = this.notificationHandlers[saneNTName] || {};
    handlers[id] = handler;
    this.notificationHandlers[saneNTName] = handlers;
    const deleteHandler = () => {
      if (this.notificationHandlers[saneNTName]) {
        delete this.notificationHandlers[saneNTName][id];
      }
    };
    return deleteHandler;
  }
  /**
   * Returns the slug (short identifier) for the current project.
   * @method
   * @async
   * @returns {string}
   */
  async projectSlug() {
    const response = await this.request("project-slug");
    return response;
  }
  /**
   * Returns the id of the current project
   * @method
   * @async
   * @returns {UUID}
   */
  async projectId() {
    const response = await this.request("project-id");
    return response;
  }
  /**
   * Save the current project to the cloud.
   * @method
   * @returns {Object<string,string>}
   */
  async saveProject() {
    const response = await this.request("save-project");
    return response;
  }
  /**
   * Loads a project from the cloud.
   * @method
   * @async
   * @param {string} identifier - Either a project id or slug
   * @returns {Object<string,string>}
   */
  async loadProject(identifier) {
    const response = await this.request("load-project", {
      identifier: String(identifier)
    });
    return response;
  }
  /**
   * Share a project: create a copy with a different id/slug and make it readonly
   * @method
   * @async
   * @returns {Object<string,string>}
   */
  async shareProject() {
    const response = await this.request("share-project");
    return response;
  }
  /**
   * List the configurators in the current design.
   * Returns an array of objects with the configurator id and pkg id.
   * @method
   * @async
   * @returns {Array<Object<string,UUID>>}
   */
  async listConfigurators() {
    const response = await this.request("list-configurators");
    return response;
  }
  /**
   * List the loaded packages
   * Returns an array of objects with the package id.
   * @method
   * @async
   * @returns {Array<Object<string,UUID>>}
   */
  async listLoadedPackages() {
    const response = await this.request("list-loaded-packages");
    return response;
  }
  /**
   * List the presets of a current package as a flat list
   * of objects with the preset id and package id.
   * @method
   * @async
   * @returns {Array<Object<string,UUID>>}
   */
  async listPresets(pkgId) {
    const response = await this.request("list-presets", { pkgId });
    return response;
  }
  /**
   * Update the current design by changing it into a preset.
   * @method
   * @async
   * @param {UUID} configuratorId
   * @param {UUID} presetId
   * @returns {boolean}
   */
  async selectPreset(configuratorId, presetId, keepDefaultMaterials = false) {
    const response = await this.request("select-preset", {
      presetId,
      configuratorId,
      keepDefaultMaterials
    });
    return response;
  }
  /**
   * Retrieves the component options (to make a new configuration)
   * @method
   * @async
   * @param {UUID} configuratorId
   * @returns {Array<Object>}
   */
  async listComponentOptions(configuratorId) {
    const response = await this.request("list-component-options", {
      configuratorId
    });
    return response;
  }
  /**
   * Retrieves the list of materials that can be applied (to make a new configuration)
   * @method
   * @async
   * @param {UUID} configuratorId
   * @returns {Array<Object>}
   */
  async listAssignableMaterials(configuratorId) {
    const response = await this.request("list-assignable-materials", {
      configuratorId
    });
    return response;
  }
  /**
   * Creates a new configuration with a new default material
   * @method
   * @async
   * @param {UUID} configuratorId
   * @param {UUID} materialId
   * @returns {boolean}
   */
  async setDefaultMaterial(configuratorId, materialId) {
    const response = await this.request("set-default-material", {
      configuratorId,
      materialId
    });
    return response;
  }
  /**
   * Take a screenshot of the current camera angle.
   * @method
   * @async
   * @returns {string} dataURI
   */
  async screenshot() {
    const response = await this.request("screenshot");
    return response;
  }
  /**
   * Send a request to the front-end layer
   * @method
   * @async
   * @param {any} data
   * @returns {any}
   */
  async ui(data) {
    const response = await this.request("ui", data);
    return response;
  }
  /**
   * Change the automatically deduced locale to a manual value
   * @method
   * @async
   * @param {string} locale, e.g. fr-FR
   * @returns {boolean}
   */
  async setLocale(locale) {
    const response = await this.request("set-locale", { locale });
    return response;
  }
  /**
   * Return the actual project price
   * @method
   * @async
   * @returns {Object}
   */
  async price() {
    const response = await this.request("price");
    return response;
  }
  /**
   * Returns a JSON export of the current project
   * @method
   * @async
   * @returns {Object}
   */
  async exportProject() {
    const response = await this.request("export-project");
    return response;
  }
};
var pb_iframe_api_default = PBIframeApi;

// src/utils/body-lock.js
var bodyLock = class {
  /**
   * lock the body to prevent scrolling.
   */
  lock() {
    const bodyElement = document.querySelector("body");
    bodyElement.setAttribute("data-body-lock", "true");
  }
  /**
   * unlock the scrolling of the body.
   */
  unlock() {
    const bodyElement = document.querySelector("body");
    bodyElement.setAttribute("data-body-lock", "false");
  }
};
var body_lock_default = new bodyLock();

// src/utils/accessibility.js
function setFocusability(container, focusable) {
  if (!container)
    return;
  const focusableElements = container.querySelectorAll("a, button, input, select, textarea, [tabindex]");
  focusableElements.forEach((element) => {
    if (focusable) {
      if (element.getAttribute("tabindex") === "-1") {
        element.removeAttribute("tabindex");
      }
    } else {
      element.setAttribute("tabindex", "-1");
    }
  });
}

// src/utils/cart-drawer.js
var cartDrawerElement = document.querySelector("#cart-drawer-holder");
var curtain = document.querySelector("#cart-curtain");
var cartDrawerOpener = document.querySelector(".cart-drawer-opener");
var closeDrawerOpener = document.querySelector(".close-cart-drawer");
var cartDrawer = class _cartDrawer {
  constructor() {
    if (cartDrawerElement) {
      if (cartDrawerOpener) {
        cartDrawerOpener.addEventListener("click", this.openCartDrawer);
      }
      if (closeDrawerOpener) {
        closeDrawerOpener.addEventListener("click", _cartDrawer.closeCartDrawer);
      }
    }
  }
  /**
   * Get current state of the cart drawer.
   *
   * @returns {{state: string, isCartOpen: boolean}}
   */
  getCurrentState() {
    if (cartDrawerElement.getAttribute("aria-hidden") === "false") {
      return {
        state: "open",
        isCartOpen: true
      };
    } else {
      return {
        state: "closed",
        isCartOpen: false
      };
    }
  }
  /**
   * Open cart drawer.
   */
  openCartDrawer() {
    cartDrawerElement.setAttribute("aria-hidden", "false");
    curtain.setAttribute("aria-hidden", "false");
    setFocusability(cartDrawerElement, true);
    body_lock_default.lock();
  }
  /**
   * Close cart drawer.
   */
  static closeCartDrawer() {
    cartDrawerElement.setAttribute("aria-hidden", "true");
    curtain.setAttribute("aria-hidden", "true");
    setFocusability(cartDrawerElement, false);
    body_lock_default.unlock();
  }
  /**
   * Handle open cart drawer.
   */
  handleCartDrawerState() {
    const currentState = this.getCurrentState();
    if (currentState.isCartOpen) {
      _cartDrawer.closeCartDrawer();
    } else {
      this.openCartDrawer();
    }
  }
  /**
   * Close the cart drawer when clicked outside the cart drawer.
   */
  static hideOnClickOutside() {
    const outsideClickListener = (event) => {
      const target = event.target;
      if (target !== cartDrawerElement && target.closest(".cart-drawer") || target.closest(".cart-drawer-opener") === cartDrawerOpener) {
      } else {
        this.closeCartDrawer();
        removeClickListener();
      }
    };
    const removeClickListener = () => {
      document.removeEventListener("click", outsideClickListener);
    };
    document.addEventListener("click", outsideClickListener);
  }
};
var cart_drawer_default = new cartDrawer();

// src/utils/get-sections.js
var getCartSectionsToRender = () => {
  const cartDrawerElement2 = document.querySelector("#cart-drawer-holder");
  const cartDrawerSectionId = cartDrawerElement2 == null ? void 0 : cartDrawerElement2.getAttribute("data-section-id");
  if (!cartDrawerSectionId) {
    console.error("Cart drawer section ID not found. Cart updates may not work properly.");
    return [];
  }
  return [
    {
      id: "cart-items",
      section: cartDrawerSectionId,
      selector: "#cart-items"
    },
    {
      id: "cart-summary",
      section: cartDrawerSectionId,
      selector: "#cart-summary"
    },
    {
      id: "cart-item-counter",
      section: "cart-item-counter",
      selector: "#cart-item-counter"
    }
  ];
};

// src/utils/cart.js
var getSectionFromHTML = (html, selector, sectionId) => {
  if (!html) {
    console.error("No HTML content provided");
    return "";
  }
  if (typeof html === "object" && html.data) {
    html = html.data;
  }
  if (typeof html === "object") {
    const sectionHTML = html.sections && sectionId ? html.sections[sectionId] : null;
    const sectionHTMLRaw = sectionId && typeof html[sectionId] === "string" ? html[sectionId] : null;
    const htmlToParse = sectionHTML != null ? sectionHTML : sectionHTMLRaw;
    if (htmlToParse) {
      try {
        const parsedHTML = new DOMParser().parseFromString(htmlToParse, "text/html");
        const selectedElement = parsedHTML.querySelector(selector);
        if (selectedElement) {
          return selectedElement.innerHTML;
        }
      } catch (error) {
        console.warn("Error parsing section HTML:", error);
      }
    }
    console.error(`Response is an object but doesn't contain usable data for section: ${selector}`);
    return "";
  }
  try {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");
    const selectedElement = parsedHTML.querySelector(selector);
    if (selectedElement) {
      return selectedElement.innerHTML;
    } else {
      console.error(`Element with selector "${selector}" not found in HTML.`);
      return "";
    }
  } catch (error) {
    console.error("Error parsing HTML:", error);
    return "";
  }
};
var handleChangeCart = (responseData) => {
  getCartSectionsToRender().forEach((section) => {
    const elementsToReplace = document.querySelectorAll(section.selector);
    if (elementsToReplace && elementsToReplace.length > 0) {
      elementsToReplace.forEach((element) => {
        const newHTML = getSectionFromHTML(
          responseData,
          section.selector,
          section.section
        );
        if (newHTML !== "") {
          element.innerHTML = newHTML;
        }
      });
    } else {
      console.warn(`No elements found for selector: ${section.selector}`);
    }
  });
};

// src/utils/format-money.js
function formatMoney(amount) {
  var _a, _b, _c, _d;
  const numericAmount = typeof amount === "number" ? amount : parseFloat(amount);
  if (!Number.isFinite(numericAmount))
    return "";
  if (typeof ((_a = window.Shopify) == null ? void 0 : _a.formatMoney) === "function") {
    return window.Shopify.formatMoney(Math.round(numericAmount * 100));
  }
  return new Intl.NumberFormat(document.documentElement.lang || "nl", {
    style: "currency",
    currency: (_d = (_c = (_b = window.Shopify) == null ? void 0 : _b.currency) == null ? void 0 : _c.active) != null ? _d : "EUR"
  }).format(numericAmount);
}

// src/utils/logger.js
var Logger = class {
  constructor() {
    this.debugEnabled = this.checkDebugMode();
    this.prefix = "[RUGABLY]";
    if (this.debugEnabled) {
      console.log(`${this.prefix} Debug mode ENABLED`);
    }
  }
  /**
   * Check if debug mode is enabled
   */
  checkDebugMode() {
    try {
      if (localStorage.getItem("rugably:debug") === "true") {
        return true;
      }
    } catch (e) {
    }
    if (typeof window !== "undefined" && window.location) {
      const params = new URLSearchParams(window.location.search);
      if (params.get("debug") === "true") {
        return true;
      }
    }
    if (typeof window !== "undefined" && window.RUGABLY_DEBUG === true) {
      return true;
    }
    return false;
  }
  /**
   * Debug level logging (only shown when debug mode is enabled)
   */
  debug(...args) {
    if (this.debugEnabled) {
      console.log(this.prefix, ...args);
    }
  }
  /**
   * Info level logging (only shown when debug mode is enabled)
   */
  info(...args) {
    if (this.debugEnabled) {
      console.info(this.prefix, ...args);
    }
  }
  /**
   * Warning level logging (always shown)
   */
  warn(...args) {
    console.warn(this.prefix, ...args);
  }
  /**
   * Error level logging (always shown)
   */
  error(...args) {
    console.error(this.prefix, ...args);
  }
  /**
   * Group logging for better organization (only shown when debug mode is enabled)
   */
  group(label) {
    if (this.debugEnabled) {
      console.group(`${this.prefix} ${label}`);
    }
  }
  /**
   * End group logging
   */
  groupEnd() {
    if (this.debugEnabled) {
      console.groupEnd();
    }
  }
  /**
   * Table logging for structured data (only shown when debug mode is enabled)
   */
  table(data) {
    if (this.debugEnabled) {
      console.table(data);
    }
  }
};
var logger_default = new Logger();

// src/utils/configurator-pattern-samples.js
function getSampleFreeLabel() {
  var _a, _b;
  return (_b = (_a = document.querySelector("sample-limit-handler[data-sample-free-label]")) == null ? void 0 : _a.dataset.sampleFreeLabel) != null ? _b : "Free";
}
async function fetchAndShowPatternSamples(handler, patternName) {
  var _a, _b, _c, _d;
  const container = document.querySelector("[data-dynamic-pattern-samples]");
  if (!container) {
    console.warn("[RUGABLY] Pattern samples: no container [data-dynamic-pattern-samples] on page");
    return;
  }
  const token = handler.dataset.storefrontApiToken || "";
  const apiVersion = handler.dataset.storefrontApiVersion || "2026-01";
  const query = `
    query getPatternSamples($query: String!) {
      products(first: 10, query: $query) {
        edges {
          node {
            id
            handle
            title
            featuredImage {
              url(transform: { maxWidth: 112, preferredContentType: WEBP })
              url2x: url(transform: { maxWidth: 224, preferredContentType: WEBP })
              altText
            }
            variants(first: 1) {
              edges {
                node {
                  id
                  price { amount }
                }
              }
            }
          }
        }
      }
    }
  `;
  const headers = { "Content-Type": "application/json" };
  if (token)
    headers["X-Shopify-Storefront-Access-Token"] = token;
  try {
    const response = await fetch(`/api/${apiVersion}/graphql.json`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        query,
        variables: { query: `${patternName} sample` }
      })
    });
    const json = await response.json().catch(() => ({}));
    if (json.errors) {
      logger_default.error("[RUGABLY] Storefront API errors:", json.errors);
      console.warn("[RUGABLY] Pattern samples: Storefront API errors", json.errors);
      renderPatternSamples(handler, []);
      return;
    }
    const products = (_d = (_c = (_b = (_a = json == null ? void 0 : json.data) == null ? void 0 : _a.products) == null ? void 0 : _b.edges) == null ? void 0 : _c.map((e) => e.node)) != null ? _d : [];
    console.info("[RUGABLY] Pattern samples: got", products.length, "products");
    renderPatternSamples(handler, products);
  } catch (err) {
    logger_default.error("[RUGABLY] Fetch pattern samples failed:", err);
    console.warn("[RUGABLY] Pattern samples: fetch failed", err);
    renderPatternSamples(handler, []);
  }
}
function renderPatternSamples(handler, products) {
  var _a;
  const container = document.querySelector("[data-dynamic-pattern-samples]");
  const templateEl = document.querySelector("[data-sample-item-template]");
  if (!container)
    return;
  if (!((_a = templateEl == null ? void 0 : templateEl.content) == null ? void 0 : _a.firstElementChild)) {
    console.warn("[RUGABLY] Pattern samples: no [data-sample-item-template] found");
    return;
  }
  console.info("[RUGABLY] Pattern samples: rendering", products.length, "in container");
  if (!products.length) {
    container.innerHTML = "";
    container.classList.add("hidden");
    return;
  }
  container.classList.remove("hidden");
  container.innerHTML = "";
  const fragment = document.createDocumentFragment();
  products.forEach((product) => {
    var _a2, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m, _n, _o, _p;
    const variant = (_c = (_b = (_a2 = product.variants) == null ? void 0 : _a2.edges) == null ? void 0 : _b[0]) == null ? void 0 : _c.node;
    const price = (_e = (_d = variant == null ? void 0 : variant.price) == null ? void 0 : _d.amount) != null ? _e : "0";
    const imageUrl = (_g = (_f = product.featuredImage) == null ? void 0 : _f.url) != null ? _g : "";
    const imageUrl2x = (_i = (_h = product.featuredImage) == null ? void 0 : _h.url2x) != null ? _i : imageUrl;
    const imageAlt = (_l = (_k = (_j = product.featuredImage) == null ? void 0 : _j.altText) != null ? _k : product.title) != null ? _l : "";
    const variantId = (_n = (_m = variant == null ? void 0 : variant.id) == null ? void 0 : _m.split("/").pop()) != null ? _n : "";
    const item = templateEl.content.firstElementChild.cloneNode(true);
    const img = item.querySelector('[data-slot="image"]');
    const titleEl = item.querySelector('[data-slot="title"]');
    const subtitleEl = item.querySelector('[data-slot="subtitle"]');
    const priceEl = item.querySelector('[data-slot="price"]');
    const addBtn = item.querySelector('[data-slot="add-button"]');
    if (img) {
      img.src = imageUrl || "";
      if (imageUrl) {
        img.srcset = `${imageUrl} 1x, ${imageUrl2x} 2x`;
        img.sizes = "56px";
      }
      img.alt = imageAlt;
      img.style.display = imageUrl ? "" : "none";
    }
    if (titleEl)
      titleEl.textContent = (_o = product.title) != null ? _o : "";
    if (subtitleEl)
      subtitleEl.textContent = (_p = product.title) != null ? _p : "";
    if (priceEl) {
      const numericPrice = parseFloat(price);
      priceEl.textContent = numericPrice === 0 ? getSampleFreeLabel() : formatMoney(price);
    }
    if (addBtn) {
      addBtn.dataset.variantId = variantId;
      addBtn.addEventListener("click", () => addPatternSampleToCart(handler, addBtn.dataset.variantId));
    }
    fragment.appendChild(item);
  });
  container.appendChild(fragment);
}
function getSelectedQualityProperty() {
  var _a, _b, _c, _d, _e;
  const sampleHandler = document.querySelector("sample-limit-handler[data-quality-property]");
  const propertyName = sampleHandler == null ? void 0 : sampleHandler.dataset.qualityProperty;
  if (!propertyName)
    return {};
  const select = sampleHandler == null ? void 0 : sampleHandler.querySelector("custom-select");
  const selectedOption = (_a = select == null ? void 0 : select.querySelector('[data-select-option][aria-selected="true"]')) != null ? _a : select == null ? void 0 : select.querySelector("[data-select-option]");
  const value = (_e = (_d = (_b = selectedOption == null ? void 0 : selectedOption.dataset) == null ? void 0 : _b.value) != null ? _d : (_c = selectedOption == null ? void 0 : selectedOption.textContent) == null ? void 0 : _c.trim()) != null ? _e : "";
  return value ? { [propertyName]: value } : {};
}
async function addPatternSampleToCart(handler, variantId) {
  if (!variantId)
    return;
  const qualityProp = getSelectedQualityProperty();
  const properties = { _is_sample: "true", ...qualityProp };
  try {
    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        properties
      })
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      document.dispatchEvent(new Event("cart:updated"));
      const cartDrawerElement2 = document.querySelector("#cart-drawer-holder");
      const cartDrawerSectionId = cartDrawerElement2 == null ? void 0 : cartDrawerElement2.getAttribute("data-section-id");
      if (cartDrawerSectionId) {
        const sectionsUrl = `${window.location.pathname}?sections=${encodeURIComponent(cartDrawerSectionId)},cart-item-counter`;
        const cartResponse = await fetch(sectionsUrl);
        const sectionsPayload = await cartResponse.json().catch(() => ({}));
        handleChangeCart({ sections: sectionsPayload });
      }
      cart_drawer_default.handleCartDrawerState();
    } else {
      alert(data.description || data.message || "Could not add sample.");
    }
  } catch (err) {
    logger_default.error("Add pattern sample failed:", err);
    alert("Could not add sample. Please try again.");
  }
}

// src/utils/configurator-image-sync.js
function _shopifySrc(src, width) {
  if (!src)
    return src;
  try {
    const url = new URL(src);
    url.searchParams.set("width", width);
    return url.toString();
  } catch (e) {
    return src;
  }
}
async function fetchAndSyncGalleryImages(handler, patternName, colorState = {}) {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l;
  if (!patternName)
    return;
  const colorName = (colorState == null ? void 0 : colorState.color) || null;
  const colorSlug = colorName ? colorName.toLowerCase().replace(/[\s_]+/g, "-") : null;
  const directHandle = colorSlug ? `${patternName.toLowerCase()}-${colorSlug}` : patternName.toLowerCase();
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 1e4);
    let product = null;
    const directRes = await fetch(`/products/${directHandle}.json`, { signal: controller.signal });
    if (directRes.ok) {
      const directData = await directRes.json().catch(() => ({}));
      if ((_b = (_a = directData == null ? void 0 : directData.product) == null ? void 0 : _a.images) == null ? void 0 : _b.length) {
        product = directData.product;
      }
    }
    if (!product) {
      const searchQuery = colorName ? `${patternName} ${colorName}` : patternName;
      const searchUrl = `/search/suggest.json?q=${encodeURIComponent(searchQuery)}&resources[type]=product&resources[limit]=10`;
      const searchRes = await fetch(searchUrl, { signal: controller.signal });
      const searchData = await searchRes.json().catch(() => ({}));
      const candidates = (_e = (_d = (_c = searchData == null ? void 0 : searchData.resources) == null ? void 0 : _c.results) == null ? void 0 : _d.products) != null ? _e : [];
      const match = (_f = candidates.find(
        (p) => !p.handle.includes("sample") && !p.title.toLowerCase().includes("sample")
      )) != null ? _f : candidates.find((p) => !p.handle.includes("sample"));
      if (match) {
        const handle = match.handle || ((_h = (_g = match.url) == null ? void 0 : _g.split("/products/")[1]) == null ? void 0 : _h.split("?")[0]);
        if (handle) {
          const productRes = await fetch(`/products/${handle}.json`, { signal: controller.signal });
          if (productRes.ok) {
            const productData = await productRes.json().catch(() => ({}));
            if (productData == null ? void 0 : productData.product)
              product = productData.product;
          }
        }
      }
    }
    clearTimeout(timeoutId);
    if (!product) {
      logger_default.debug("[RUGABLY] Image sync: no product found for", directHandle);
      return;
    }
    const variants = (_i = product.variants) != null ? _i : [];
    let targetVariantId = (_k = (_j = variants[0]) == null ? void 0 : _j.id) != null ? _k : null;
    if (colorName && variants.length > 1) {
      const colorLower = colorName.toLowerCase();
      const matched = variants.find(
        (v) => {
          var _a2, _b2, _c2;
          return ((_a2 = v.title) != null ? _a2 : "").toLowerCase().includes(colorLower) || ((_b2 = v.option1) != null ? _b2 : "").toLowerCase().includes(colorLower) || ((_c2 = v.option2) != null ? _c2 : "").toLowerCase().includes(colorLower);
        }
      );
      if (matched)
        targetVariantId = matched.id;
    }
    let rawImages = (_l = product.images) != null ? _l : [];
    if (targetVariantId !== null && rawImages.some((img) => {
      var _a2;
      return ((_a2 = img.variant_ids) == null ? void 0 : _a2.length) > 0;
    })) {
      const filtered = rawImages.filter(
        (img) => {
          var _a2;
          return !((_a2 = img.variant_ids) == null ? void 0 : _a2.length) || img.variant_ids.includes(targetVariantId);
        }
      );
      if (filtered.length > 0)
        rawImages = filtered;
    }
    if (!rawImages.length) {
      logger_default.debug("[RUGABLY] Image sync: no images for", directHandle);
      return;
    }
    const images = rawImages.map((img) => ({
      url: img.src,
      altText: img.alt || product.title || patternName,
      width: img.width,
      height: img.height
    }));
    _updateGallery(images, product.title || patternName);
  } catch (err) {
    if ((err == null ? void 0 : err.name) !== "AbortError") {
      logger_default.error("[RUGABLY] Image sync: fetch failed", err);
    }
  }
}
function _updateGallery(images, productTitle) {
  const gallery = document.querySelector("media-gallery");
  const list = gallery ? gallery.querySelector('ul[id^="Slider-Thumbnails"]') : null;
  if (!gallery || !list) {
    logger_default.debug("[RUGABLY] Image sync: <media-gallery> or thumbnail list not found");
    return;
  }
  const sectionId = list.id.replace("Slider-Thumbnails-", "");
  const itemClass = "max-md:grid max-md:w-screen max-md:snap-start max-md:shrink-0 aspect-square";
  const wrapperClass = "aspect-square overflow-hidden rounded-lg";
  const imgClass = "block object-cover w-full h-full";
  const fragment = document.createDocumentFragment();
  images.forEach((image, i) => {
    const li = document.createElement("li");
    li.setAttribute("data-media-id", `${sectionId}-synced-${i}`);
    li.className = itemClass;
    const picture = document.createElement("picture");
    picture.className = wrapperClass;
    picture.style.display = "block";
    const img = document.createElement("img");
    img.src = _shopifySrc(image.url, 720);
    img.srcset = [360, 720, 1080, 1440].map((w) => `${_shopifySrc(image.url, w)} ${w}w`).join(", ");
    img.sizes = "(max-width: 768px) 40vw, 35vw";
    img.alt = image.altText || productTitle;
    img.loading = i === 0 ? "eager" : "lazy";
    img.decoding = "async";
    img.className = imgClass;
    if (image.width)
      img.width = image.width;
    if (image.height)
      img.height = image.height;
    picture.appendChild(img);
    li.appendChild(picture);
    fragment.appendChild(li);
  });
  const currentHeight = list.offsetHeight;
  if (currentHeight > 0) {
    list.style.minHeight = `${currentHeight}px`;
  }
  list.style.transition = "opacity 0.15s ease";
  list.style.opacity = "0";
  list.addEventListener(
    "transitionend",
    () => {
      list.innerHTML = "";
      list.appendChild(fragment);
      if (typeof gallery.refreshItems === "function") {
        gallery.refreshItems();
      }
      requestAnimationFrame(() => {
        list.style.minHeight = "";
        list.style.opacity = "1";
      });
    },
    { once: true }
  );
}

// src/utils/configurator-add-to-cart.js
function parseConfigurationProperties(descriptionHtml) {
  const properties = {};
  if (!descriptionHtml)
    return properties;
  const text = descriptionHtml.replace(/<[^>]*>/g, "").trim();
  const lines = text.split("\n").map((line) => line.trim()).filter((line) => line);
  lines.forEach((line) => {
    const match = line.match(/^-?\s*([^:]+):\s*(.+)$/);
    if (match) {
      const key = match[1].trim();
      const value = match[2].trim();
      const formattedKey = key.charAt(0).toUpperCase() + key.slice(1);
      properties[formattedKey] = value;
    }
  });
  logger_default.debug("Parsed properties:", properties);
  return properties;
}
function showLoadingToast(handler) {
  if (handler.loadingToastEl)
    return;
  const overlay = document.createElement("div");
  overlay.setAttribute("data-configurator-loading-toast", "");
  overlay.style.cssText = `
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0,0,0,0.4);
  `;
  const toast = document.createElement("div");
  toast.style.cssText = `
    background: white;
    color: #1a1a1a;
    padding: 1.25rem 1.75rem;
    border-radius: 0.5rem;
    box-shadow: 0 10px 40px rgba(0,0,0,0.15);
    font-size: 1rem;
    line-height: 1.5;
    text-align: center;
    max-width: 90%;
  `;
  toast.textContent = "Wij zijn je tapijt aan het samenstellen, een momentje geduld alstublieft.";
  overlay.appendChild(toast);
  document.body.appendChild(overlay);
  handler.loadingToastEl = overlay;
}
function hideLoadingToast(handler) {
  if (!handler.loadingToastEl)
    return;
  handler.loadingToastEl.remove();
  handler.loadingToastEl = null;
}
async function addConfiguredRugToCart(handler, variantId) {
  var _a, _b, _c, _d;
  if (handler.addToCartInProgress) {
    logger_default.debug("Add to cart already in progress, ignoring duplicate request");
    return;
  }
  handler.addToCartInProgress = true;
  showLoadingToast(handler);
  if (handler.cartDrawerContent) {
    handler.cartDrawerContent.setAttribute("aria-busy", true);
  }
  const maxAttempts = 3;
  const initialDelayMs = 5e3;
  const retryDelayMs = 2500;
  let lastRetryableMessage = null;
  const properties = parseConfigurationProperties((_c = (_b = (_a = window.rugablyConfig) == null ? void 0 : _a.product) == null ? void 0 : _b.descriptionHtml) != null ? _c : "");
  if (handler.pbApi && typeof handler.pbApi.pbinfo === "object" && ((_d = handler.pbApi.pbinfo) == null ? void 0 : _d.originAllowed) && typeof handler.pbApi.exportProject === "function") {
    try {
      const exportResponse = await handler.pbApi.exportProject();
      const exportPayload = exportResponse == null ? void 0 : exportResponse.data;
      if (exportPayload != null) {
        const exportStr = typeof exportPayload === "string" ? exportPayload : JSON.stringify(exportPayload);
        properties["Configuration export"] = exportStr;
      }
    } catch (e) {
      logger_default.debug("PB export-project not used for cart:", e);
    }
  }
  const formData = new FormData();
  formData.append("id", variantId);
  formData.append("quantity", "1");
  Object.keys(properties).forEach((key) => {
    formData.append(`properties[${key}]`, properties[key]);
  });
  logger_default.debug("Adding to cart with properties:", Object.keys(properties));
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    if (attempt === 1) {
      await new Promise((resolve) => setTimeout(resolve, initialDelayMs));
    } else {
      await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
    }
    let response;
    let data = {};
    try {
      response = await fetch("/cart/add.js", {
        method: "POST",
        body: formData
      });
      data = await response.json().catch(() => ({}));
    } catch (networkError) {
      logger_default.error("Error adding to cart (network):", networkError);
      handler.addToCartInProgress = false;
      hideLoadingToast(handler);
      if (handler.cartDrawerContent)
        handler.cartDrawerContent.setAttribute("aria-busy", false);
      alert("Could not add to cart. Please check your connection and try again.");
      return;
    }
    if (response.ok) {
      logger_default.debug("Successfully added to cart:", data);
      handler.addToCartInProgress = false;
      hideLoadingToast(handler);
      if (handler.cartDrawerContent)
        handler.cartDrawerContent.setAttribute("aria-busy", false);
      const cartDrawerElement2 = document.querySelector("#cart-drawer-holder");
      const cartDrawerSectionId = cartDrawerElement2 == null ? void 0 : cartDrawerElement2.getAttribute("data-section-id");
      if (cartDrawerSectionId) {
        const sectionsUrl = `${window.location.pathname}?sections=${encodeURIComponent(cartDrawerSectionId)},cart-item-counter`;
        const cartResponse = await fetch(sectionsUrl);
        const sectionsPayload = await cartResponse.json().catch(() => ({}));
        handleChangeCart({ sections: sectionsPayload });
      } else {
        logger_default.error("Cart drawer section ID not found, skipping section update");
      }
      document.dispatchEvent(new Event("cart:updated"));
      logger_default.debug("Cart updated successfully");
      cart_drawer_default.handleCartDrawerState();
      return;
    }
    if (response.status === 422 || response.status === 429) {
      lastRetryableMessage = data.description || data.message || (response.status === 429 ? "Too many requests" : "Variant not yet available");
      logger_default.debug(`Add to cart attempt ${attempt}/${maxAttempts} failed (${response.status}), next try in ${retryDelayMs}ms...`);
      continue;
    }
    const message = data.description || data.message || `Request failed (${response.status}). Please try again.`;
    logger_default.error("Cart add failed:", response.status, data);
    handler.addToCartInProgress = false;
    hideLoadingToast(handler);
    if (handler.cartDrawerContent)
      handler.cartDrawerContent.setAttribute("aria-busy", false);
    alert(message);
    return;
  }
  handler.addToCartInProgress = false;
  hideLoadingToast(handler);
  if (handler.cartDrawerContent)
    handler.cartDrawerContent.setAttribute("aria-busy", false);
  alert("This item is not ready to add yet. Please wait a moment and try adding to cart again from the configurator.");
  logger_default.error("Add to cart failed after retries:", lastRetryableMessage);
}

// src/utils/configurator-price-parser.js
var CURRENCY_TEXT_PATTERN = /(?:[€$£]\s*[\d][\d\s.,]*|\b(?:EUR|USD|GBP)\b\s*[\d][\d\s.,]*)/i;
var PRICE_KEY_PATTERN = /(price|amount|gross|vat|tax|cents|formatted|display|label)/i;
var FORMAT_KEYS = ["formatted", "display", "priceFormatted", "displayPrice", "label"];
var WRAPPER_KEYS = ["data", "summary", "result", "payload", "price", "totals"];
var MINOR_UNIT_KEYS = [
  "centsIncludingTax",
  "cents_including_tax",
  "cents",
  "amountCents",
  "priceCents"
];
var MAJOR_UNIT_KEYS = [
  "amountIncludingTax",
  "amountInclVat",
  "priceIncludingTax",
  "priceInclVat",
  "gross",
  "grossAmount",
  "totalPrice",
  "finalPrice",
  "finalTotal",
  "amount",
  "price"
];
function parseNumber(value) {
  if (typeof value === "number" && Number.isFinite(value))
    return value;
  if (typeof value !== "string")
    return null;
  const text = value.trim();
  if (!text)
    return null;
  const cleaned = text.replace(/[^0-9,.-]/g, "");
  if (!cleaned)
    return null;
  const commaCount = (cleaned.match(/,/g) || []).length;
  const dotCount = (cleaned.match(/\./g) || []).length;
  const lastComma = cleaned.lastIndexOf(",");
  const lastDot = cleaned.lastIndexOf(".");
  let normalized = cleaned;
  if (commaCount > 0 && dotCount > 0) {
    normalized = lastComma > lastDot ? cleaned.replace(/\./g, "").replace(",", ".") : cleaned.replace(/,/g, "");
  } else if (commaCount > 1) {
    normalized = cleaned.replace(/,/g, "");
  } else if (dotCount > 1) {
    normalized = cleaned.replace(/\./g, "");
  } else if (commaCount === 1 && dotCount === 0) {
    const decimals = cleaned.length - lastComma - 1;
    normalized = decimals === 3 ? cleaned.replace(",", "") : cleaned.replace(",", ".");
  } else if (dotCount === 1 && commaCount === 0) {
    const decimals = cleaned.length - lastDot - 1;
    normalized = decimals === 3 ? cleaned.replace(".", "") : cleaned;
  }
  const parsed = parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}
function resolveAmount(value, divisor = 1) {
  const numeric = parseNumber(value);
  if (numeric == null)
    return null;
  const amount = numeric / divisor;
  if (!Number.isFinite(amount) || amount < 0)
    return null;
  return {
    formattedText: null,
    amount
  };
}
function parseFormattedCurrencyText(value) {
  if (typeof value !== "string")
    return null;
  const text = value.trim().replace(/\s+/g, " ");
  if (!text || !CURRENCY_TEXT_PATTERN.test(text))
    return null;
  const amount = parseNumber(text);
  if (amount == null || amount < 0)
    return null;
  return {
    formattedText: text,
    amount: null
  };
}
function resolveFromPriceObject(price, seen = /* @__PURE__ */ new WeakSet(), depth = 0) {
  if (price == null || depth > 8)
    return null;
  if (typeof price === "number" || typeof price === "string") {
    const formatted = parseFormattedCurrencyText(price);
    if (formatted)
      return formatted;
    return resolveAmount(price);
  }
  if (Array.isArray(price)) {
    for (const entry of price) {
      const resolved = resolveFromPriceObject(entry, seen, depth + 1);
      if (resolved)
        return resolved;
    }
    return null;
  }
  if (typeof price !== "object")
    return null;
  if (seen.has(price))
    return null;
  seen.add(price);
  for (const key of MINOR_UNIT_KEYS) {
    const resolved = resolveAmount(price[key], 100);
    if (resolved)
      return resolved;
  }
  for (const key of MAJOR_UNIT_KEYS) {
    const value = price[key];
    if (value == null)
      continue;
    const resolved = typeof value === "object" ? resolveFromPriceObject(value, seen, depth + 1) : resolveAmount(value);
    if (resolved)
      return resolved;
  }
  for (const key of FORMAT_KEYS) {
    const formatted = parseFormattedCurrencyText(price[key]);
    if (formatted)
      return formatted;
  }
  for (const key of WRAPPER_KEYS) {
    const resolved = resolveFromPriceObject(price[key], seen, depth + 1);
    if (resolved)
      return resolved;
  }
  for (const [key, value] of Object.entries(price)) {
    if (!PRICE_KEY_PATTERN.test(key))
      continue;
    const resolved = resolveFromPriceObject(value, seen, depth + 1);
    if (resolved)
      return resolved;
  }
  return null;
}
function extractPriceFromEventData(eventData) {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l;
  return [
    (_a = eventData == null ? void 0 : eventData.data) == null ? void 0 : _a.price,
    eventData == null ? void 0 : eventData.price,
    (_c = (_b = eventData == null ? void 0 : eventData.data) == null ? void 0 : _b.summary) == null ? void 0 : _c.price,
    (_d = eventData == null ? void 0 : eventData.summary) == null ? void 0 : _d.price,
    (_e = eventData == null ? void 0 : eventData.data) == null ? void 0 : _e.totals,
    (_f = eventData == null ? void 0 : eventData.data) == null ? void 0 : _f.summary,
    eventData == null ? void 0 : eventData.data,
    eventData,
    (_l = (_k = (_j = (_i = (_h = (_g = eventData == null ? void 0 : eventData.items) == null ? void 0 : _g.product) == null ? void 0 : _h.variants) == null ? void 0 : _i.edges) == null ? void 0 : _j[0]) == null ? void 0 : _k.node) == null ? void 0 : _l.price
  ].filter((candidate) => candidate != null);
}
function resolvePriceFromPayload(payload) {
  var _a, _b, _c, _d, _e, _f;
  if (payload == null)
    return null;
  const candidates = [
    (_a = payload == null ? void 0 : payload.data) == null ? void 0 : _a.price,
    payload == null ? void 0 : payload.price,
    (_c = (_b = payload == null ? void 0 : payload.data) == null ? void 0 : _b.summary) == null ? void 0 : _c.price,
    (_d = payload == null ? void 0 : payload.summary) == null ? void 0 : _d.price,
    (_e = payload == null ? void 0 : payload.data) == null ? void 0 : _e.totals,
    payload == null ? void 0 : payload.totals,
    (_f = payload == null ? void 0 : payload.data) == null ? void 0 : _f.summary,
    payload == null ? void 0 : payload.summary,
    payload == null ? void 0 : payload.data,
    payload
  ];
  for (const candidate of candidates) {
    const resolved = resolveFromPriceObject(candidate);
    if (resolved)
      return resolved;
  }
  return null;
}
function getEventResolvedPrice(eventPriceCandidates) {
  for (const candidate of eventPriceCandidates) {
    const resolved = resolvePriceFromPayload(candidate);
    if (resolved)
      return resolved;
  }
  return null;
}

// src/utils/configurator-price-sync.js
var PRICE_DISPLAY_SELECTOR = "#configurator-price-display";
var PRICE_SYNC_DEBOUNCE_MS = 120;
function isPbPriceEvent(eventData) {
  return (eventData == null ? void 0 : eventData.target) === "pb" && ((eventData == null ? void 0 : eventData.type) === "price" || (eventData == null ? void 0 : eventData.type) === "price-change");
}
function updateConfiguratorPriceDisplay(priceValue) {
  const priceDisplayEl = document.querySelector(PRICE_DISPLAY_SELECTOR);
  if (!priceDisplayEl)
    return;
  if (priceValue == null ? void 0 : priceValue.formattedText) {
    priceDisplayEl.textContent = priceValue.formattedText;
    return;
  }
  if (Number.isFinite(priceValue == null ? void 0 : priceValue.amount) && priceValue.amount >= 0) {
    priceDisplayEl.textContent = formatMoney(priceValue.amount);
    return;
  }
  throw new Error("Resolved price has no renderable value");
}
function getResolvedEventPrice(eventData) {
  const eventCandidates = extractPriceFromEventData(eventData);
  return getEventResolvedPrice(eventCandidates);
}
async function getResolvedPbApiPrice(handler) {
  if (!handler.pbApi || typeof handler.pbApi.price !== "function") {
    throw new Error("ProductBuilder price API is not available");
  }
  const pbApi = typeof handler._connectPbApi === "function" ? await handler._connectPbApi() : handler.pbApi;
  if (!pbApi || typeof pbApi.price !== "function") {
    throw new Error("ProductBuilder price API could not connect");
  }
  const priceResponse = await pbApi.price();
  const pbApiResolvedPrice = resolvePriceFromPayload(priceResponse);
  if (!pbApiResolvedPrice) {
    throw new Error("Unable to resolve ProductBuilder price payload");
  }
  return pbApiResolvedPrice;
}
async function syncConfiguratorPrice(handler, eventData) {
  try {
    const eventResolvedPrice = getResolvedEventPrice(eventData);
    const pbPriceEvent = isPbPriceEvent(eventData);
    let pbApiResolvedPrice = null;
    if (!pbPriceEvent || !eventResolvedPrice) {
      try {
        pbApiResolvedPrice = await getResolvedPbApiPrice(handler);
      } catch (pbApiError) {
        if (!eventResolvedPrice)
          throw pbApiError;
      }
    }
    const resolvedPrice = pbPriceEvent ? eventResolvedPrice != null ? eventResolvedPrice : pbApiResolvedPrice : pbApiResolvedPrice != null ? pbApiResolvedPrice : eventResolvedPrice;
    if (!resolvedPrice) {
      throw new Error("Unable to resolve configurator price");
    }
    updateConfiguratorPriceDisplay(resolvedPrice);
  } catch (err) {
    logger_default.error("Configurator price sync failed:", err);
  }
}
function queueConfiguratorPriceSync(handler, eventData) {
  handler._pendingConfiguratorPriceEventData = eventData;
  if (handler._configuratorPriceSyncTimer)
    return;
  handler._configuratorPriceSyncTimer = window.setTimeout(() => {
    const latestEventData = handler._pendingConfiguratorPriceEventData;
    handler._pendingConfiguratorPriceEventData = null;
    handler._configuratorPriceSyncTimer = null;
    void syncConfiguratorPrice(handler, latestEventData);
  }, PRICE_SYNC_DEBOUNCE_MS);
}

// src/utils/configurator-iframe-messages.js
function shouldSyncPrice(message) {
  var _a;
  return isPbPriceEvent(message) || (message == null ? void 0 : message.type) === "config-changed" || (message == null ? void 0 : message.action) === "pbShopifyProductCreate" && Boolean((_a = message == null ? void 0 : message.items) == null ? void 0 : _a.product);
}
function resolveColorState(message) {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m, _n, _o, _p;
  const findNestedColorState = (payload, seen = /* @__PURE__ */ new WeakSet(), depth = 0) => {
    if (!payload || typeof payload !== "object" || depth > 8)
      return {};
    if (seen.has(payload))
      return {};
    seen.add(payload);
    if (typeof payload.pattern === "string") {
      return {
        pattern: payload.pattern,
        color: typeof payload.color === "string" ? payload.color : void 0
      };
    }
    for (const value of Object.values(payload)) {
      const nested = findNestedColorState(value, seen, depth + 1);
      if (nested == null ? void 0 : nested.pattern)
        return nested;
    }
    return {};
  };
  const candidates = [
    (_b = (_a = message == null ? void 0 : message.data) == null ? void 0 : _a.state) == null ? void 0 : _b.color,
    (_c = message == null ? void 0 : message.state) == null ? void 0 : _c.color,
    (_f = (_e = (_d = message == null ? void 0 : message.data) == null ? void 0 : _d.project) == null ? void 0 : _e.state) == null ? void 0 : _f.color,
    (_h = (_g = message == null ? void 0 : message.project) == null ? void 0 : _g.state) == null ? void 0 : _h.color,
    (_k = (_j = (_i = message == null ? void 0 : message.data) == null ? void 0 : _i.configuration) == null ? void 0 : _j.state) == null ? void 0 : _k.color,
    (_m = (_l = message == null ? void 0 : message.configuration) == null ? void 0 : _l.state) == null ? void 0 : _m.color,
    (_o = (_n = message == null ? void 0 : message.data) == null ? void 0 : _n.project) == null ? void 0 : _o.color,
    (_p = message == null ? void 0 : message.project) == null ? void 0 : _p.color
  ];
  const directMatch = candidates.find((candidate) => candidate && typeof candidate === "object");
  if (directMatch)
    return directMatch;
  return findNestedColorState(message);
}
function syncPatternDependentUi(handler, message) {
  const colorState = resolveColorState(message);
  const patternName = colorState == null ? void 0 : colorState.pattern;
  if (!patternName)
    return;
  const colorName = typeof (colorState == null ? void 0 : colorState.color) === "string" ? colorState.color : "";
  const stateKey = `${patternName}::${colorName}`;
  if (handler._lastPatternSyncStateKey === stateKey)
    return;
  handler._lastPatternSyncStateKey = stateKey;
  if (handler.dataset.enablePatternSamples !== void 0) {
    fetchAndShowPatternSamples(handler, patternName);
  }
  if (handler.dataset.enableImageSync !== void 0) {
    fetchAndSyncGalleryImages(handler, patternName, colorState);
  }
}
function handleIframeMessage(handler, event) {
  var _a;
  if (!handler.allowedOrigins.includes(event.origin))
    return;
  if (!event.data || typeof event.data !== "object")
    return;
  const message = event.data;
  if (shouldSyncPrice(message)) {
    queueConfiguratorPriceSync(handler, message);
  }
  syncPatternDependentUi(handler, message);
  if (message.type === "config-changed") {
    return;
  }
  if (message.action !== "pbShopifyProductCreate")
    return;
  const productData = (_a = message.items) == null ? void 0 : _a.product;
  if (!productData)
    return;
  const defaultVariantId = productData.defaultVariantId;
  if (defaultVariantId == null)
    return;
  const numericVariantId = defaultVariantId.split("/").pop();
  window.rugablyConfig = {
    action: message.action,
    product: productData,
    variantId: productData.defaultVariantId,
    numericVariantId,
    timestamp: (/* @__PURE__ */ new Date()).toISOString()
  };
  addConfiguredRugToCart(handler, numericVariantId);
}

// src/extenders/configurator-handler.js
var ConfiguratorHandler = class extends HTMLElement {
  /**
   * @returns {void}
   */
  constructor() {
    super();
    this.allowedOrigins = [
      "https://acceptance.productbuilder.nl",
      "https://live.productbuilder.cloud",
      "https://dev.productbuilder.cloud"
    ];
    this.cartDrawerContent = document.querySelector("#cart-drawer-content");
    this.addToCartInProgress = false;
    this.loadingToastEl = null;
    this.pbApi = null;
    this._pbApiConnectPromise = null;
    window.addEventListener("message", (event) => handleIframeMessage(this, event));
  }
  /**
   * Initialize iframe API bridge once iframe is present and loaded.
   * @returns {void}
   */
  connectedCallback() {
    const iframe = this.querySelector("iframe");
    if (!iframe)
      return;
    this.pbApi = new pb_iframe_api_default(iframe);
    iframe.addEventListener("load", () => {
      void this._connectPbApi().catch((error) => {
        logger_default.error("PB Iframe API connect failed:", error);
      });
    });
  }
  /**
   * Connect to the ProductBuilder iframe API (target: 'pb' protocol).
   * Used for export-project, price, etc.
   * The promise is memoized; on failure memoization is cleared so retries can occur.
   * @returns {Promise<PBIframeApi>}
   */
  async _connectPbApi() {
    if (this._pbApiConnectPromise)
      return this._pbApiConnectPromise;
    if (!this.pbApi)
      throw new Error("PB Iframe API is not initialized");
    this._pbApiConnectPromise = this.pbApi.connect().then(() => this.pbApi).catch((error) => {
      this._pbApiConnectPromise = null;
      throw error;
    });
    return this._pbApiConnectPromise;
  }
};
customElements.define("configurator-handler", ConfiguratorHandler);
